import { Outlet, Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { GlobalSearchBar } from '@/components/GlobalSearchBar'
import { Sidebar } from '@/components/Sidebar'
import { NavGroup } from '@/components/NavGroup'
import { NavItem } from '@/components/NavItem'
import { CommandPalette } from '@/components/CommandPalette'
import { Breadcrumbs } from '@/components/Breadcrumbs'
import { BreadcrumbProvider } from '@/contexts/BreadcrumbContext'
import { getVisibleNavGroups } from '@/config/navigationConfig'
import { useSidebarCollapsed } from '@/hooks/use-sidebar-collapsed'
import { useRecordRecentRoute } from '@/hooks/use-recent-routes'
import { ViewSwitcher } from '@/components/ViewSwitcher'
import { HrPreviewBar } from '@/components/HrPreviewBar'
import { useSocket } from '@/hooks/use-socket'
import { Menu } from 'lucide-react'
import { NotificationBell } from '@/components/NotificationBell'
import { LogIncomingCallDialog } from '@/components/LogIncomingCallDialog'
import { useState, useEffect } from 'react'
import logo from '@/assets/GrackleDocs_BirdOnly.png'
import { UserMenu } from '@/components/UserMenu'
import { ImpersonationBanner } from '@/components/ImpersonationBanner'
import { EnvironmentBadge } from '@/components/EnvironmentBadge'
import { MaintenancePage } from '@/components/MaintenancePage'
import { Wrench } from 'lucide-react'
import { GlobalAnnouncement } from '@/components/GlobalAnnouncement'
import { useGlobalAnnouncements } from '@/hooks/useGlobalAnnouncements'
import { StartupDialogs } from '@/components/welcome-back/StartupDialogs'
import { socketClient } from '@/lib/socket'
import { useUser } from '@/contexts/UserContext'
import { useIntegrationStatus } from '@/hooks/use-api'
import { AlertTriangle } from 'lucide-react'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'

/**
 * Persistent reminder to set up the Google (Gmail/Calendar) sync connection
 * under Settings. Driven entirely by live status — visible whenever sync is not
 * active, gone the instant it is. No client-side flag. Skips ticketing-only
 * Support users and impersonation sessions. NOTE: this is the secondary Settings
 * sync connection, not the OAuth login (which is automatic for Google logins).
 */
function GoogleConnectionBanner() {
  const { data: status, isLoading } = useIntegrationStatus()
  const { userType, isImpersonating } = useUser()

  // Don't nag while impersonating or for ticketing-only Support users.
  if (isImpersonating || userType === 'Support') return null
  if (isLoading || !status) return null

  // Show the banner only when Google is not connected at all, or a previously
  // connected token needs reconnecting. sync_emails/sync_calendar are user
  // preferences (Calendar-only or "tokens only" are valid), so they must not
  // trigger the "connect" nag.
  const expired = Boolean((status as any).needs_reconnect)
  if (status.connected && !expired) return null

  return (
    <div
      className="mb-4 flex items-center gap-3 rounded-md bg-destructive px-4 py-2 text-white"
      role="status"
      aria-live="polite"
    >
      <AlertTriangle className="h-4 w-4 flex-shrink-0 text-white" aria-hidden="true" />
      <p className="text-sm text-white">
        {expired
          ? 'Your Google connection has expired — reconnect to keep syncing your email and calendar. '
          : 'Connect your Google account to sync your email and calendar with the CRM. '}
        <Link
          to="/settings"
          className="font-semibold underline hover:text-white/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-destructive rounded"
        >
          {expired ? 'Reconnect in Settings' : 'Go to Settings → Google'}
        </Link>
      </p>
    </div>
  )
}

export default function Layout() {
  const { isConnected } = useSocket()
  const { isImpersonating, isAdmin, effectiveUserType } = useUser()
  const [collapsed, setCollapsed] = useSidebarCollapsed()
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const isHrArea = useLocation().pathname.startsWith('/hr')
  const mobileGroups = getVisibleNavGroups(effectiveUserType).filter((g) => !g.hideFromSidebar)

  // Track recently visited pages for the sidebar + command palette.
  useRecordRecentRoute()
  const [incomingCallDialogOpen, setIncomingCallDialogOpen] = useState(false)
  const [maintenanceMode, setMaintenanceMode] = useState<{
    enabled: boolean
    message: string
  } | null>(null)
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('gracklecrm_dark_mode') === 'true'
  })
  const { announcements, addAnnouncement, dismissAnnouncement } = useGlobalAnnouncements()

  // Sync dark mode to DOM and localStorage
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode)
    localStorage.setItem('gracklecrm_dark_mode', String(darkMode))
  }, [darkMode])

  // Listen for global announcements via socket
  useEffect(() => {
    socketClient.onAnnouncement(addAnnouncement)
    return () => {
      socketClient.offAnnouncement(addAnnouncement)
    }
  }, [addAnnouncement])

  // Listen for maintenance mode changes (Socket.IO + API 503)
  useEffect(() => {
    const handleSocketMaintenance = (data: { enabled: boolean; message: string }) => {
      setMaintenanceMode(data)
    }
    const handleApiMaintenance = (e: Event) => {
      const detail = (e as CustomEvent).detail
      setMaintenanceMode(detail)
    }
    socketClient.onMaintenance(handleSocketMaintenance)
    window.addEventListener('maintenance-mode', handleApiMaintenance)
    return () => {
      socketClient.offMaintenance(handleSocketMaintenance)
      window.removeEventListener('maintenance-mode', handleApiMaintenance)
    }
  }, [])

  // Non-admin users see full maintenance page
  if (maintenanceMode?.enabled && !isAdmin) {
    return <MaintenancePage message={maintenanceMode.message} />
  }

  return (
    <div className={cn('min-h-screen bg-background', isImpersonating && 'pt-[52px]')}>
      {/* Maintenance Mode Banner - Admin only */}
      {maintenanceMode?.enabled && isAdmin && (
        <div
          className="bg-[#FFC857] text-[#072744] px-4 py-2 text-center text-sm font-medium flex items-center justify-center gap-2"
          role="alert"
          aria-live="polite"
        >
          <Wrench className="h-4 w-4" aria-hidden="true" />
          Maintenance mode is ON — only admins can access the system
        </div>
      )}

      {/* Impersonation Banner - Fixed at very top when active */}
      <ImpersonationBanner />

      {/* Global Announcements Banner - Shows Closed Won celebrations */}
      <GlobalAnnouncement announcements={announcements} onDismiss={dismissAnnouncement} />
      {/* Welcome-back summary and What's New, taking turns. Mounted here rather than in App so
          they only exist behind authentication; they gate themselves on the user resolving. Not
          reached from PipedriveLayout, which does not use Layout. */}
      <StartupDialogs />

      {/* Skip to main content link for accessibility */}
      <a href="#main-content" className="skip-to-main">
        Skip to main content
      </a>

      {/* Header with two-row navigation - adjust top position when impersonating */}
      <header
        className={cn(
          'sticky z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60',
          isImpersonating ? 'top-[52px]' : 'top-0'
        )}
      >
        {/* Row 1: Logo, Search, Controls, User Info */}
        <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8 border-b gap-4">
          <div className="flex items-center gap-4 flex-shrink-0">
            {/* Mobile Navigation Drawer Trigger - Only visible on mobile */}
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden"
                  aria-label="Toggle navigation menu"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] sm:w-[320px]">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <img src={logo} alt="Grackle CRM" className="h-6 w-6 object-contain" />
                    <span>Navigation</span>
                  </SheetTitle>
                </SheetHeader>
                <nav className="mt-6 space-y-0.5" aria-label="Mobile navigation">
                  {mobileGroups.map((group) => {
                    if (group.standalone && group.href) {
                      return (
                        <NavItem
                          key={group.id}
                          label={group.label}
                          href={group.href}
                          icon={group.icon}
                          onNavigate={() => setMobileNavOpen(false)}
                        />
                      )
                    }
                    if (group.items) {
                      return (
                        <NavGroup
                          key={group.id}
                          id={group.id}
                          label={group.label}
                          icon={group.icon}
                          items={group.items}
                          defaultExpanded={group.defaultExpanded}
                          onNavigate={() => setMobileNavOpen(false)}
                        />
                      )
                    }
                    return null
                  })}
                </nav>
              </SheetContent>
            </Sheet>

            <div className="flex items-center gap-2">
              <img src={logo} alt="Grackle CRM" className="h-8 w-8 object-contain" />
              <EnvironmentBadge />
              <ViewSwitcher />
            </div>
          </div>

          {/* Global Search Bar - Now visible on all screen sizes */}
          <div className="flex-1 max-w-4xl mx-2 md:mx-4">
            <GlobalSearchBar />
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <NotificationBell />

            <UserMenu
              darkMode={darkMode}
              onToggleDarkMode={() => setDarkMode((prev) => !prev)}
              isConnected={isConnected}
              onLogIncomingCall={() => setIncomingCallDialogOpen(true)}
            />
          </div>
        </div>
      </header>

      {/* Desktop Sidebar Navigation - Hidden on mobile */}
      <div className="hidden lg:block">
        <Sidebar collapsed={collapsed} onToggleCollapsed={() => setCollapsed()} />
      </div>

      {/* Global command palette (Cmd/Ctrl+K) */}
      <CommandPalette />

      {/* Main content — left padding tracks the sidebar width (rail vs full) */}
      <BreadcrumbProvider>
        <main
          id="main-content"
          className={cn('p-4 sm:p-6 lg:p-8', collapsed ? 'lg:pl-[88px]' : 'lg:pl-[272px]')}
          tabIndex={-1}
        >
          {/* Salesforce-style breadcrumb trail (Home > Object > Record) */}
          <Breadcrumbs />
          {/* Google sync reminder — lives in the content column so it never overlaps the fixed sidebar/header */}
          <GoogleConnectionBanner />
          {/* HR "preview as" control/banner — mounted only in the HR workspace so
              its HR queries don't fire on CRM pages */}
          {isHrArea && <HrPreviewBar />}
          <Outlet />
        </main>
      </BreadcrumbProvider>

      {/* Log Incoming Call Dialog */}
      <LogIncomingCallDialog
        open={incomingCallDialogOpen}
        onOpenChange={setIncomingCallDialogOpen}
      />
    </div>
  )
}
